import { useEffect } from 'react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';
import { reportError, toError } from '../observability/error-reporter';
import { ErrorFallback } from './ErrorFallback';
import { NotFound } from './NotFound';

/**
 * React Router's `errorElement`.
 *
 * Router-level errors never reach a React error boundary — the router catches
 * them first, on the way through a loader, an action, or a lazy route module.
 * Without this, a failed dynamic import renders a blank page and reports
 * nothing.
 *
 * A 404 is not a failure, so it is not reported and does not offer a retry.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const isNotFound = isRouteErrorResponse(error) && error.status === 404;

  useEffect(() => {
    if (isNotFound) return;
    reportError({
      error: toError(error),
      componentStack: null,
      context: { boundary: 'route' },
    });
  }, [error, isNotFound]);

  if (isNotFound) return <NotFound />;

  return (
    <ErrorFallback
      titleKey="errors:route.title"
      descriptionKey="errors:route.description"
      onRetry={() => {
        void navigate(0);
      }}
    />
  );
}
